import {
  CircleIcon,
  ArrowRight02Icon,
  AddCircleIcon,
  Calendar04Icon,
  File02Icon,
  UserCircleIcon,
  CheckmarkCircle01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import moment from "moment";
import {
  ActivityConfigMap,
  DATE_FORMATS,
  RelatedMapping
} from "shared/app/TaskTableView/constants";
import {
  ActivityLog,
  AssigneeChange,
  AssigneesChangedMetadata,
  DueDateChangedMetadata,
  RelatedSubjectChangedMetadata,
  TaskActivity,
  TitleOrDescritionChangedMetadata
} from "shared/app/TaskTableView/types";
const ICON_MAP: Record<string, any> = {
  CircleIcon,
  AddCircleIcon,
  Calendar04Icon,
  File02Icon,
  UserCircleIcon,
  CheckmarkCircle01Icon
};

const getIconByName = (iconName: string) => ICON_MAP[iconName] || CircleIcon;
const TitleOrDescriptionData = (props: {
  metadata: TitleOrDescritionChangedMetadata;
}) => {
  return (
    <div className="flex items-center gap-2 text-sm text-content-400 w-full min-w-0">
      <span className="truncate shrink min-w-0">
        {props.metadata.original_rich_text.document_text || "None"}
      </span>
      <HugeiconsIcon
        icon={ArrowRight02Icon}
        className="text-content-100 flex-shrink-0"
        size={20}
      />
      <span className="truncate shrink min-w-0">
        {props.metadata.changed_rich_text.document_text}
      </span>
    </div>
  );
};

const DueDateData = (props: { metadata: DueDateChangedMetadata }) => {
  const original = props.metadata.original_due_date
    ? moment(props.metadata.original_due_date).format(DATE_FORMATS.DATE)
    : "None";
  const changed = props.metadata.changed_due_date
    ? moment(props.metadata.changed_due_date).format(DATE_FORMATS.DATE)
    : "None";
  return (
    <div className="flex items-center gap-2 text-sm text-content-400 w-full min-w-0">
      <time className="truncate shrink min-w-0">{original}</time>
      <HugeiconsIcon
        icon={ArrowRight02Icon}
        className="text-content-100 flex-shrink-0"
        size={20}
      />
      <time className="truncate shrink min-w-0">{changed}</time>
    </div>
  );
};

const RelatedSubjectData = (props: {
  metadata: RelatedSubjectChangedMetadata;
}) => {
  const original = props.metadata.original_related_subject;
  const changed = props.metadata.changed_related_subject;

  const originalDisplay = original
    ? `${RelatedMapping[original.subject_type]}: ${original.subject_name}`
    : "None";

  const changedDisplay = changed
    ? `${RelatedMapping[changed.subject_type]}: ${changed.subject_name}`
    : "None";

  return (
    <div className="flex items-center gap-2 text-sm text-content-400 w-full min-w-0">
      <span className="truncate shrink min-w-0">{originalDisplay}</span>
      <HugeiconsIcon
        icon={ArrowRight02Icon}
        className="text-content-100 flex-shrink-0"
        size={20}
      />
      <span className="truncate shrink min-w-0">{changedDisplay}</span>
    </div>
  );
};

const AssigneesData = (props: { metadata: AssigneesChangedMetadata }) => {
  const getNames = (assignees: AssigneeChange[]) =>
    assignees.map((a) => a.assignee_name).join(", ") || "None";
  return (
    <div className="flex items-center gap-2 text-sm text-content-400 w-full min-w-0">
      <span className="truncate shrink min-w-0">
        {getNames(props.metadata.original_assignees)}
      </span>
      <HugeiconsIcon
        icon={ArrowRight02Icon}
        className="text-content-100 flex-shrink-0"
        size={20}
      />
      <span className="truncate shrink min-w-0">
        {getNames(props.metadata.changed_assignees)}
      </span>
    </div>
  );
};

export const ActivityItemData = (log: ActivityLog, creator: string) => {
  const title = log.title;
  const metadata = log.metadata;
  const activityType = log.activity_type as TaskActivity;
  const icon = getIconByName(ActivityConfigMap[activityType].iconName);

  if (
    activityType === TaskActivity.CHANGE_TASK_TITLE ||
    activityType === TaskActivity.CHANGE_TASK_DESCRIPTION
  )
    return {
      title,
      icon,
      sub: TitleOrDescriptionData({
        metadata: metadata as TitleOrDescritionChangedMetadata
      })
    };
  if (activityType === TaskActivity.CHANGE_TASK_DUE)
    return {
      title,
      icon,
      sub: <DueDateData metadata={metadata as DueDateChangedMetadata} />
    };
  if (activityType === TaskActivity.CHANGE_TASK_SUBJECT)
    return {
      title,
      icon,
      sub: (
        <RelatedSubjectData
          metadata={metadata as RelatedSubjectChangedMetadata}
        />
      )
    };
  if (activityType === TaskActivity.CHANGE_TASK_ASSIGNEE)
    return {
      title,
      icon,
      sub: <AssigneesData metadata={metadata as AssigneesChangedMetadata} />
    };
  if (activityType === TaskActivity.COMPLETE_TASK)
    return {
      title,
      sub: `Completed by: ${creator}`,
      icon
    };
  if (activityType === TaskActivity.REOPEN_TASK)
    return { title, sub: `Re-opened by: ${creator}`, icon };
  return { title, sub: `Created by: ${creator}`, icon };
};
